/**
 * Collapses the long tail of a stacked-bar series into a single "Other" segment.
 *
 * Why: the taxa stacked bar renders one chart series per distinct taxon at the current level,
 * with no cap. At deep levels (genus/species) that can be hundreds–thousands of series, of which
 * only the largest ~20 are visible — the rest are sub-pixel, unidentifiable (legend hidden,
 * colours cycle) and effectively unclickable, yet still cost crunch + paint. This trims them.
 *
 * The collapse is **faithful**: "Other" carries the summed magnitude of every dropped taxon, so
 * bar heights (and percentage totals) are unchanged — only the unreadable tail merges visually.
 *
 * ## Configuration (code-only for now — tweak {STACKED_BAR_CAP} and hot-reload)
 *
 *   - `none`      — render everything (original behaviour).
 *   - `topN`      — keep the `topN` largest taxa by total magnitude.
 *   - `threshold` — keep any taxon reaching `minShare` of some bucket's total.
 *   - `both`      — keep a taxon only if it's in the top-N *and* clears the share threshold.
 *
 * All four modes are wired so the behaviour can be compared without a code change beyond editing
 * the config object. No UX toggle yet (intentionally).
 */
export type StackedBarCapMode = 'none' | 'topN' | 'threshold' | 'both'

export interface StackedBarCapConfig {
  mode: StackedBarCapMode
  /** Used by `topN` and `both`. */
  topN: number
  /** Used by `threshold` and `both`. Fraction 0..1 of a bucket's total (0.005 = 0.5%). */
  minShare: number
}

/** Edit me to experiment. */
export const STACKED_BAR_CAP: StackedBarCapConfig = {
  mode: 'topN',
  topN: 20,
  minShare: 0.005
}

/** Series key + tooltip label for the collapsed remainder. Sentinel; not a real taxon. */
export const OTHER_SERIES_KEY = 'Other'

type Row = Record<string, number | string>

export interface CollapseResult {
  data: Row[]
  stackKeys: string[]
  /** The key used for the remainder series, or `null` when nothing was collapsed. */
  otherKey: string | null
}

/**
 * @param data       one row per bucket: `{ xAxisvalue: label, [taxonKey]: number, … }`
 * @param stackKeys  the taxon keys present in `data` (numeric columns)
 */
export function collapseStackedSeries(data: Row[], stackKeys: string[], cfg: StackedBarCapConfig = STACKED_BAR_CAP): CollapseResult {
  if (cfg.mode === 'none' || stackKeys.length === 0) {
    return { data, stackKeys, otherKey: null }
  }

  const bucketTotals = data.map((row) => {
    let t = 0
    for (const k of stackKeys) t += (row[k] as number) ?? 0
    return t
  })

  const stats = stackKeys.map((key) => {
    let sum = 0
    let maxShare = 0
    for (let b = 0; b < data.length; b++) {
      const v = (data[b]![key] as number) ?? 0
      sum += v
      const total = bucketTotals[b]!
      if (total > 0) maxShare = Math.max(maxShare, v / total)
    }
    return { key, sum, maxShare }
  })

  const topNKeys = new Set(
    [...stats]
      .sort((a, b) => b.sum - a.sum)
      .slice(0, Math.max(0, cfg.topN))
      .map((s) => s.key)
  )

  const keep = (s: { key: string; maxShare: number }): boolean => {
    switch (cfg.mode) {
      case 'topN':
        return topNKeys.has(s.key)
      case 'threshold':
        return s.maxShare >= cfg.minShare
      case 'both':
        return topNKeys.has(s.key) && s.maxShare >= cfg.minShare
      default:
        return true
    }
  }

  const keptKeys = stats.filter(keep).map((s) => s.key)
  if (keptKeys.length === stackKeys.length) {
    return { data, stackKeys, otherKey: null } // nothing dropped → leave untouched
  }

  const keptSet = new Set(keptKeys)
  let anyOther = false

  const newData: Row[] = data.map((row) => {
    const out: Row = {}
    let other = 0
    for (const [k, v] of Object.entries(row)) {
      if (typeof v !== 'number') {
        out[k] = v // axis label (xAxisvalue) and any other non-numeric field
      } else if (keptSet.has(k)) {
        out[k] = v
      } else {
        other += v
      }
    }
    if (other > 0) {
      out[OTHER_SERIES_KEY] = other
      anyOther = true
    }
    return out
  })

  const newStackKeys = [...keptKeys].sort((a, b) => a.localeCompare(b))
  if (anyOther) newStackKeys.push(OTHER_SERIES_KEY) // last so it sits consistently in the stack

  return { data: newData, stackKeys: newStackKeys, otherKey: anyOther ? OTHER_SERIES_KEY : null }
}
